import React, { useCallback, useMemo } from 'react'
import { ChartOptionElement, ReportElement as GqlReportElement } from 'gqlTypes'
import KeyRatingsChartElementRow, {
  KeyRatingsChartElementRowProps,
} from 'components/KeyRatingsChartElement/Row'
import { Box, Collapse, Paper, Typography } from '@mui/material'
import useChartOptions from 'hooks/useChartOptions'
import ReportElementSettings from './ReportElementSettings'
import KeyRatingsChartElementHeader from 'components/KeyRatingsChartElement/Header'
import useReportDetails from 'hooks/useReportDetails'
import getChartTitle from 'util/getChartTitle'
import { ReportElementProps } from './ReportElement'
import useKeyMeasureRow from 'hooks/useKeyMeasureRow'
import useReportElementChart from 'hooks/useReportElementChart'

const ReportElementKeyRatingsChart: React.FC<ReportElementProps> = ({
  reportId,
  element,
  sectionId,
  report,
  favorites = false,
  print = false,
  onUpdateFavorites,
}) => {
  const { handleSetSort } = useReportElementChart(element, reportId)
  const { parsedData } = useReportDetails(reportId)
  const parsedContent = useMemo(
    () => JSON.parse(element.content || 'null'),
    [element.content]
  )
  // This has caused problems a few times with KM charts. Probably worth spending some time here at some point
  // dup in useReportElementChart. We should clean this up.
  const data =
    parsedData?.elements && parsedContent.data_source_name
      ? parsedData.elements[parsedContent.data_source_name]
      : {}
  const elementTitle = getChartTitle(parsedContent, data)

  const { cohort, config, onChangeConfig } = useChartOptions(
    element.id,
    ChartOptionElement.Element,
    undefined,
    reportId
  )

  const handleSetSubgroupItem = (subgroup: string, subgroupItem?: string) => {
    if (
      subgroup === 'Overall' ||
      subgroupItem === 'Overall' ||
      subgroup === 'None' ||
      subgroupItem === 'None'
    ) {
      onChangeConfig({
        ...config,
        subgroup: undefined,
        subgroupItem: undefined,
      })
    } else {
      if (!subgroupItem) {
        const row = parsedContent.rows[0]
        const dataIdent = row.type === 'toggledBarChart' ? 'series' : 'values'
        const items =
          row?.segmentations.filter(
            (s: { name: string; values: { name: string }[] }) =>
              s.name === subgroup
          )[0][dataIdent] || []
        subgroupItem = items[0]?.name
      }
      onChangeConfig({
        ...config,
        subgroup: subgroup,
        subgroupItem: subgroupItem,
      })
    }
  }

  const rows = parsedContent.rows || []
  const showHideOptions = rows.map((item: { id: number; title: string }) => {
    return { key: item.title, title: item.title }
  })

  const handleSetHidden = useCallback(
    (value: string[]) => {
      onChangeConfig({ ...config, hidden: { km: value } })
    },
    [onChangeConfig]
  )

  if (rows.length === 0) {
    return null
  }

  const showTrend =
    (!config?.subgroup || !config?.subgroupItem) && rows[0].has_past_data
  const pastCol = rows[0].static_current?.name
    ? 'static_past_results'
    : 'past_results'

  const showTrendNa = showTrend && (rows[0][pastCol] || []).length === 0
  const showTrendCol = showTrend || showTrendNa
  const everStatic =
    rows.filter(
      (r: { static_current?: { name: string } }) => r.static_current?.name
    ).length > 0

  rows.sort(
    (a: KeyRatingsChartElementRowProps, b: KeyRatingsChartElementRowProps) => {
      const { sortValue: sortValueA } = useKeyMeasureRow(
        a,
        config?.subgroup,
        config?.subgroupItem,
        a.prefix,
        a.suffix,
        everStatic,
        config?.sort?.value || 'sort_order'
      )
      const { sortValue: sortValueB } = useKeyMeasureRow(
        b,
        config?.subgroup,
        config?.subgroupItem,
        b.prefix,
        b.suffix,
        everStatic,
        config?.sort?.value || 'sort_order'
      )
      if (config?.sort && config?.sort?.direction === 'desc') {
        return (sortValueA - sortValueB) * -1
      }
      return (sortValueA || 0) - (sortValueB || 0)
    }
  )

  if (print) {
    const numberOfColumns = showTrendCol ? 4 : 3
    return (
      <table
        style={{
          width: '1200px',
          borderBottom: '1px solid #E0E0E0',
          marginBottom: '32px',
        }}
      >
        <thead>
          {elementTitle && (
            <tr style={{ breakInside: 'avoid' }}>
              <th colSpan={numberOfColumns} style={{ textAlign: 'left' }}>
                <Typography variant="h5">{elementTitle}</Typography>
                <Box mt={2} mb={2}>
                  <ReportElementSettings
                    print={print}
                    reportId={reportId}
                    element={element}
                    sectionId={sectionId}
                    report={report}
                    noPastResults
                    showHideOptions={showHideOptions}
                    hidden={config?.hidden?.km || []}
                    onChangeSubgroupItem={handleSetSubgroupItem}
                    onChangeHidden={handleSetHidden}
                    favorites={favorites}
                    onUpdateFavorites={onUpdateFavorites}
                  />
                </Box>
              </th>
            </tr>
          )}
          <tr style={{ breakInside: 'avoid' }}>
            <th
              style={{
                paddingLeft: '32px',
                width: '300px',
                textAlign: 'left',
                fontWeight: 'normal',
              }}
            >
              Key Measure
            </th>
            <th
              style={{
                paddingLeft: '32px',
                width: '200px',
                fontWeight: 'normal',
              }}
            >
              Average
            </th>
            <th
              style={{
                paddingLeft: '32px',
                width: showTrendCol ? '400px' : '636px',
                paddingRight: showTrendCol ? '0px' : '32px',
                textAlign: 'left',
                fontWeight: 'normal',
              }}
            >
              Percentile Rank
            </th>
            {showTrendCol && (
              <th
                style={{
                  paddingLeft: '32px',
                  width: '300px',
                  textAlign: 'left',
                  fontWeight: 'normal',
                }}
              >
                Trend Data
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map((r: KeyRatingsChartElementRowProps, i: number) => {
            if (!(config?.hidden?.km || []).includes(r.title)) {
              return (
                <KeyRatingsChartElementRow
                  {...r}
                  key={`keyRatingsRow${i}`}
                  data={r}
                  reportId={reportId}
                  cohort={cohort}
                  subgroup={config?.subgroup}
                  subgroupItem={config?.subgroupItem}
                  everStatic={everStatic}
                  print={print}
                />
              )
            }
          })}
        </tbody>
      </table>
    )
  }

  return (
    <Box sx={{ width: '100%', mb: 4 }}>
      <Box px={print ? 0 : 4}>
        {elementTitle && <Typography variant="h5">{elementTitle}</Typography>}
      </Box>
      <Box mt={2} mb={2}>
        <ReportElementSettings
          print={print}
          reportId={reportId}
          element={element}
          sectionId={sectionId}
          report={report}
          noPastResults
          showHideOptions={showHideOptions}
          hidden={config?.hidden?.km || []}
          onChangeSubgroupItem={handleSetSubgroupItem}
          onChangeHidden={handleSetHidden}
          favorites={favorites}
          onUpdateFavorites={onUpdateFavorites}
        />
      </Box>
      <Box px={print ? 0 : 4} py={4}>
        <KeyRatingsChartElementHeader
          sort={config?.sort?.value || 'sort_order'}
          direction={config?.sort?.direction}
          onSort={handleSetSort}
          showTrendCol={showTrendCol}
          print={print}
        />
        {rows.map((r: KeyRatingsChartElementRowProps, i: number) => (
          <Collapse
            key={r.title}
            in={!(config?.hidden?.km || []).includes(r.title)}
          >
            <KeyRatingsChartElementRow
              {...r}
              data={r}
              reportId={reportId}
              cohort={cohort}
              subgroup={config?.subgroup}
              subgroupItem={config?.subgroupItem}
              everStatic={everStatic}
              print={print}
            />
          </Collapse>
        ))}
      </Box>
    </Box>
  )
}

export default ReportElementKeyRatingsChart
